/**
 * useChat hook: Manages chat state and API communication
 */

import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  success?: boolean;
  error?: string;
  screenshot?: string | null;
  bugReportType?: 'bug' | null;
}

export type ChatMode = 'agent' | 'ask';

interface UseChatContext {
  currentRoute?: string;
  selectedFounders?: string[];
  onRefreshFounders?: () => void;
  activeTab?: { id: string; name: string } | null;
}

export interface ActionLogData {
  transcript?: string;
  extractedActions?: Array<{
    actionId: string;
    params: any;
    confidence: number;
  }>;
  executed?: Array<{
    actionId: string;
    params: any;
    status?: string;
    message?: string;
  }>;
  alreadyExists?: Array<{
    actionId: string;
    params: any;
    status?: string;
    message?: string;
  }>;
  failed?: Array<{
    actionId: string;
    reason: string;
  }>;
}

export function useChat(userId: string, context?: UseChatContext) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLogData, setActionLogData] = useState<ActionLogData | null>(null);
  const [showActionLog, setShowActionLog] = useState(false);
  const [mode, setMode] = useState<ChatMode>('agent');
  const [bugReportType, setBugReportType] = useState<'bug' | null>(null);
  const [screenshot, setScreenshot] = useState<string | null>(null);

  // Initialize session - always start fresh
  useEffect(() => {
    // Clear any existing session to ensure fresh start
    localStorage.removeItem('chatSessionId');
    setSessionId(null);
  }, []);

  const sendMessage = useCallback(async (message: string, screenshotData?: string | null, bugType?: 'bug' | null) => {
    // Add user message immediately with screenshot if available
    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date(),
      screenshot: screenshotData || screenshot,
      bugReportType: bugType || bugReportType,
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Build context with bug report info if available
    const requestContext: any = {
      currentRoute: context?.currentRoute,
      selectedFounders: context?.selectedFounders,
      activeTab: context?.activeTab,
    };
    
    // Add bug report type and screenshot if available
    if (bugType || bugReportType) {
      requestContext.bugReportType = bugType || bugReportType;
    }
    if (screenshotData || screenshot) {
      requestContext.screenshot = screenshotData || screenshot;
    }

    try {
      const response = await axios.post(`${API_BASE}/api/conversation`, {
        message,
        userId,
        sessionId,
        mode,
        context: requestContext,
      });

      const data = response.data;

      // Update session ID
      if (data.sessionId) {
        setSessionId(data.sessionId);
        localStorage.setItem('chatSessionId', data.sessionId);
      }

      // Handle response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: data.reply,
        timestamp: new Date(),
        success: !data.error,
        error: data.error,
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Clear screenshot and bug report type after successful submission if it was a bug report
      if (bugType || bugReportType) {
        // Check if the reply indicates successful submission
        if (data.reply && (data.reply.includes('submitted') || data.reply.includes('Successfully') || data.reply.includes('view it here'))) {
          setScreenshot(null);
          setBugReportType(null);
        }
      }

      // Check if a founder was added (look for "Added" in the reply or actionResult)
      // This ensures the table refreshes immediately when founders are added via chat
      if (data.reply && /added|Added|✅/i.test(data.reply)) {
        console.log('🔄 Founder added via chat, refreshing table...');
        if (context?.onRefreshFounders) {
          // If we're on the People view, refresh the table directly
          setTimeout(() => {
            context.onRefreshFounders!();
          }, 300);
        } else {
          // If we're not on the People view, force a page refresh after a delay
          console.log('🔄 Not on People view, will refresh page to show new founder...');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      }

      // Also refresh if we have actionResult with a founder
      if (data.actionResult && data.actionResult.id) {
        console.log('🔄 Founder added via actionResult, refreshing table...');
        if (context?.onRefreshFounders) {
          setTimeout(() => {
            context.onRefreshFounders!();
          }, 300);
        } else {
          // If we're not on the People view, force a page refresh after a delay
          console.log('🔄 Not on People view, will refresh page to show new founder...');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      }

      // Handle conversation ended with actions
      if (data.conversationEnded && data.extractedActions && data.extractedActions.length > 0) {
        console.log('🎯 Actions extracted:', data.extractedActions);
        console.log('✅ Executed:', data.executedCount, '❌ Failed:', data.failedCount);
        
        // Show action summary if actions were executed or already exist
        if (data.executedCount > 0 || data.alreadyExistsCount > 0) {
          // Show messages for already_exists actions
          if (data.alreadyExistsCount > 0 && data.alreadyExists) {
            data.alreadyExists.forEach((action: any) => {
              if (action.message) {
                const alreadyExistsMessage: ChatMessage = {
                  role: 'assistant',
                  content: `ℹ️ ${action.message}`,
                  timestamp: new Date(),
                  success: true,
                };
                setMessages(prev => [...prev, alreadyExistsMessage]);
              }
            });
          }
          
          // Show generic success message for regular executed actions
          if (data.executedCount > 0) {
            const actionSummary: ChatMessage = {
              role: 'assistant',
              content: `✅ Executed ${data.executedCount} action${data.executedCount > 1 ? 's' : ''} from our conversation.${data.summary ? ` Summary: ${data.summary}` : ''}`,
              timestamp: new Date(),
              success: true,
            };
            setMessages(prev => [...prev, actionSummary]);
          }
        }
      }


    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error',
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, sessionId, context, mode, bugReportType, screenshot]);

  const endConversation = useCallback(async () => {
    console.log('🚪 endConversation called with sessionId:', sessionId, 'userId:', userId);
    if (!sessionId || !userId) {
      console.log('❌ Cannot end conversation - missing sessionId or userId');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/api/conversation/end`, {
        sessionId,
        userId,
        mode,
      });

      const data = response.data;

      // Store action log data and show modal
      setActionLogData({
        transcript: data.transcript,
        extractedActions: data.extractedActions,
        executed: data.executed,
        alreadyExists: data.alreadyExists,
        failed: data.failed
      });
      setShowActionLog(true);

      // Refresh founder data if refresh function is provided
      if (context?.onRefreshFounders) {
        console.log('🔄 Refreshing founder data after conversation end');
        context.onRefreshFounders();
      }

      // Clear session completely
      localStorage.removeItem('chatSessionId');
      setSessionId(null);
      setMessages([]); // Clear messages for fresh start
    } catch (error) {
      console.error('Error ending conversation:', error);
    }
  }, [sessionId, userId, mode, context]);

  const startNewSession = useCallback(() => {
    console.log('🆕 Starting new session');
    localStorage.removeItem('chatSessionId');
    setSessionId(null);
    setMessages([]);
    setActionLogData(null);
    setShowActionLog(false);
  }, []);

  return {
    messages,
    sendMessage,
    isLoading,
    sessionId,
    endConversation,
    startNewSession,
    actionLogData,
    showActionLog,
    setShowActionLog,
    mode,
    setMode,
    bugReportType,
    setBugReportType,
    screenshot,
    setScreenshot,
  };
}

